import __ from "src/utils/Translations";
import { dynamicNotification } from "src/utils/Notifications";
import {
  downloadMedia,
  fetchEmployeeAbsenceRequestPdf,
  fetchExportFile,
  fetchImportTemplate,
} from "src/Pages/Company/Absences/api/api";
import { ImportType } from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecords.models";

const getMimeType = (extension: string): string => {
  switch (true) {
    case ["png", "jpg", "jpeg"].includes(extension):
      return "image/*";
    case ["avi", "mp4", "mov"].includes(extension):
      return "video/*";
    case extension === "pdf":
      return "application/pdf";
    case extension === "doc":
      return "application/msword";
    case extension === "docx":
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    case extension === "csv":
      return "text/csv";
    case extension === "xlsx":
      return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    default:
      throw new Error(`Unsupported extension ${extension}`);
  }
};

const saveFile = (blob: Blob, fileName: string, openInNewTab: boolean) => {
  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob);
  if (openInNewTab) {
    link.target = "_blank";
  } else {
    link.download = fileName;
  }
  link.click();
};

const extractFileNameFromContentDisposition = (
  contentDisposition: string,
  defaultFileName: string
): string => {
  const filenameMatch = contentDisposition.match(
    /filename\*=UTF-8''([^;]+)|filename="?=\?UTF-8\?Q\?([^?]+)\?="?|filename="?([^";]+)"?/i
  );

  // eslint-disable-next-line no-nested-ternary
  return filenameMatch
    ? // eslint-disable-next-line no-nested-ternary
      filenameMatch[1]
      ? decodeURIComponent(filenameMatch[1])
      : filenameMatch[2]
      ? decodeURIComponent(filenameMatch[2].replace(/=/g, "%"))
      : decodeURIComponent(filenameMatch[3])
    : defaultFileName;
};

export default () => {
  const downloadFile = async (
    params: Record<string, string>,
    fileName: string,
    openInNewTab = false
  ) => {
    const extensionProcessed = fileName.split(".").pop();
    const fileNameParsed = fileName.replace(".", "_");
    try {
      const response = await downloadMedia(params);
      const file = await response.arrayBuffer();
      const blob = new Blob([file], {
        type: getMimeType(extensionProcessed ?? ""),
      });

      saveFile(blob, fileNameParsed, openInNewTab);
    } catch {
      dynamicNotification(
        __("Wystąpił błąd pobierania pliku."),
        "error",
        "top-right",
        7000
      );
    }
  };

  return downloadFile;
};

// TODO: make function specific for exports - maybe get from Dashboard
export const downloadExportFile = async (exportId: string) => {
  try {
    const { data, contentDisposition } = await fetchExportFile(exportId);

    const fileName = extractFileNameFromContentDisposition(
      contentDisposition,
      "export.csv"
    );
    const extensionProcessed = fileName.split(".").pop();

    const file = await data.arrayBuffer();
    const blob = new Blob([file], {
      type: getMimeType(extensionProcessed || ""),
    });

    saveFile(blob, fileName, false);
  } catch {
    dynamicNotification(
      __("Wystąpił błąd pobierania pliku."),
      "error",
      "top-right",
      7000
    );
  }
};

export const downloadAbsenceRequestPdf = async (absenceRequestId: string) => {
  try {
    const { data, contentDisposition } = await fetchEmployeeAbsenceRequestPdf(
      absenceRequestId
    );

    const fileName = contentDisposition
      ? extractFileNameFromContentDisposition(
          contentDisposition,
          `${absenceRequestId}.pdf`
        )
      : `${absenceRequestId}.pdf`;

    const blob = new Blob([data], {
      type: getMimeType("pdf"),
    });

    saveFile(blob, fileName, false);
  } catch {
    dynamicNotification(
      __("Wystąpił błąd pobierania pliku."),
      "error",
      "top-right",
      7000
    );
  }
};

export const downloadImportTemplateFile = async (importType: ImportType) => {
  try {
    const { data, headers } = await fetchImportTemplate(importType);

    const fileName = headers["content-disposition"]
      ? extractFileNameFromContentDisposition(
          headers["content-disposition"],
          "import-template.xlsx"
        )
      : "import-template.xlsx";

    const blob = new Blob([data], {
      type: getMimeType("xlsx"),
    });

    saveFile(blob, fileName, false);
  } catch {
    dynamicNotification(
      __("Wystąpił błąd pobierania pliku."),
      "error",
      "top-right",
      7000
    );
  }
};
